import { CONSOLE_CAPTURE_PREFIX, DEBUG_ASSERTIONS } from "@ascent/debugger";
import type { ServerTabData } from "@ascent/debugger/runtime";
import type { Response as SearchResponse } from "@ascent/papi-core";
import type {
  Body as ServerRequestPayload,
  Response as ServerResponse,
} from "@rampjs/server";
import clientPkg from "../package.json";
import { setupClickTracking } from "./beacon-click-tracking";
import { runEnrichment, type RunEnrichmentResult } from "./enrichment";
import * as injectableUnits from "./injectable-units";
import { createUnitGate } from "./rsoc-gate";
import { getResponse } from "./server";
import { trackingRegistry, type PartnerInitResult } from "./tracking-registry";

// =============================================================================
// MODULE REGISTRATION
// =============================================================================
// Import the modules barrel to auto-register all tracking modules (cheq,
// facebook, google-csa, bing). Each module self-registers with the tracking
// registry on import. See src/modules/index.ts for the registration order.
// The bing binders are imported from the module itself so this barrel stays
// the registration entry.
// =============================================================================
import "./modules";
import { bindBingInstrumentation, bindBingTelemetry } from "./modules/bing";

type DebugOverlayParams = {
  server?: ServerResponse;
  requestPayload?: ServerRequestPayload;
  error?: unknown;
  rampJsServerUrl?: string;
  enrichment?: RunEnrichmentResult;
};

type DebugDynamicValue = NonNullable<
  NonNullable<ServerTabData["dynamicData"]>[string]
>;

function loadDebuggerRuntime() {
  return import("@ascent/debugger/runtime");
}

async function showDebugOverlay({
  server,
  requestPayload,
  error,
  rampJsServerUrl,
  enrichment,
}: DebugOverlayParams) {
  const {
    getAscentConsoleLogs,
    showOverlay,
    createOverlayTabs,
    buildOverviewTabData,
  } = await loadDebuggerRuntime();
  // Local consent-tab wrapper adds the "Tracking Partners" section (registry
  // init results) on top of the shared consent tab — matches the serp/renderer
  // debug panel (ABE-697).
  const { ensureConsentTab, populateConsentTabData } = await import(
    "./debug/consent-tab"
  );
  const { ensureOverridesTab, populateOverridesTabData } = await import(
    "./debug/overrides-tab"
  );

  ensureConsentTab();
  ensureOverridesTab();

  const tabs = createOverlayTabs();

  tabs.Console = getAscentConsoleLogs();

  if (error instanceof Error || typeof error === "string") {
    tabs["Error"] = {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    };
    tabs["Overview"] = {
      "RampJS Client Version": clientPkg.version,
      "Request Status": "FAILED",
    };
  } else if (!server) {
    // Pending paint: neither a response nor an error yet. Render immediately so
    // the panel (Console + Overrides, which need no server data) is usable at
    // once, with an obvious indicator that the request is still in flight. Off
    // the VPN the server fetch hangs for the browser's connection timeout
    // (~5–10s); without this the panel would not appear until then. `main()`
    // re-invokes showDebugOverlay with the settled server/error to fill in the
    // rest — showOverlay preserves the active tab across re-renders.
    tabs["Overview"] = {
      "RampJS Client Version": clientPkg.version,
      "Request Status": "⏳ Waiting for server response…",
    };
  } else {
    const dynamicData: ServerTabData["dynamicData"] = {
      "Full RampJs Server Object": server as unknown as DebugDynamicValue,
      "RampJs Request Payload": requestPayload as unknown as DebugDynamicValue,
    };
    if (server._debug) {
      dynamicData["API Debug Info (_debug)"] =
        server._debug as unknown as DebugDynamicValue;
    }
    if (enrichment) {
      dynamicData["Enrichment Engine"] =
        enrichment as unknown as DebugDynamicValue;
    }
    const changes: ServerTabData["changes"] = Array.isArray(server.changes)
      ? (server.changes as ServerTabData["changes"])
      : undefined;

    const serverTabData: ServerTabData = {
      response: server.search?.response as ServerTabData["response"],
      rawResponse: server.rawSearch?.response as ServerTabData["rawResponse"],
      changes,
      dynamicData,
    };

    tabs.Server = serverTabData;

    tabs["Overview"] = buildOverviewTabData(server.search?.response, {
      "RampJS Client Version": clientPkg.version,
      "RampJS Server Version": server.debug?.serverVersion,
      "PAPI Core URL": server.papiCoreUrl?.toString(),
      "RampJS Server URL": rampJsServerUrl,
      Enrichment: enrichment?.skipped
        ? `skipped (${enrichment.reason ?? ""})`
        : enrichment?.error
        ? `error: ${enrichment.error}`
        : typeof enrichment?.placed === "number"
        ? `placed ${enrichment.placed}`
        : undefined,
    });
  }

  populateConsentTabData(tabs);
  populateOverridesTabData(tabs);
  showOverlay(tabs);
}

function bindBingAfterDeferredPlacement(
  response: SearchResponse,
  trackingResults: PartnerInitResult[],
): void {
  const bing = trackingResults.find((result) => result.id === "bing");
  if (
    !bing ||
    bing.status === "skipped_disabled" ||
    bing.status === "skipped_no_consent" ||
    bing.status === "error"
  ) {
    return;
  }
  if (bing.status === "initialized_limited") {
    bindBingTelemetry(response);
    return;
  }
  bindBingInstrumentation(response);
}

export async function main() {
  if (__DEBUG__) {
    DEBUG_ASSERTIONS.length = 0; // Reset once at app start
    // Paint the overlay immediately with a "waiting" indicator so it is usable
    // while the server request is still in flight (off-VPN the fetch can hang
    // ~5–10s). The success/catch paths below re-render it with the real data.
    // Guard on DOM readiness — the overlay appends to document.body.
    if (document.readyState === "loading") {
      addEventListener("DOMContentLoaded", () => void showDebugOverlay({}), {
        once: true,
      });
    } else {
      await showDebugOverlay({});
    }
  }
  try {
    const { server, requestPayload, rampJsServerUrl } = await getResponse();

    // ABE-850: when `render_options.rampjs.defer_units_until_rsoc` is on, hold
    // injectable-unit placement until RSoC resolves (or times out) so widgets
    // never compete with it. Created before RSoC initializes so no callback can
    // slip past it. `undefined` = nothing to wait for; place immediately.
    const unitGate = createUnitGate(server);
    const searchResponse =
      server.search?.response ??
      ({} as NonNullable<typeof server.search>["response"]);

    // One pass over server.units. Each declared unit is appended once. A second
    // pass would stack another copy of every unit into the same container.
    // applyUnitsHead installs the page-level selectTier loader + geo beacon the
    // renderer emitted in server.unitsHead. The head is deferred along with the
    // units: its loader competes with RSoC too.
    const placeUnits = () => {
      injectableUnits.applyUnitsHead(server);
      injectableUnits.onResponse(server);
    };

    // Ungated pages place before tracking scans the document. Bing dwell,
    // visibility, and telemetry bind on DOM-ready, and a publisher page is
    // usually already parsed by the time ramp.js runs.
    if (!unitGate) {
      placeUnits();
    }

    // Initialize all registered tracking partners (cheq, facebook, google-csa,
    // bing) through the consent-aware registry. Behaviour is preserved from the
    // previous hand-wired sequence: the modules are consent-exempt, so they run
    // regardless of consent state exactly as ramp.js did before.
    const trackingResults = await trackingRegistry.initializeAll(
      searchResponse,
    );

    if (__DEBUG__) {
      console.log(
        `${CONSOLE_CAPTURE_PREFIX} Tracking Partners:`,
        trackingResults,
      );
    }

    // Must not block click tracking / enrichment below. Bing's first scan
    // ran before these units existed, so bind it to the single placement.
    // Resolves either way, so enrichment never waits on a failed placement.
    const deferredPlacement = unitGate
      ?.then(() => {
        placeUnits();
        bindBingAfterDeferredPlacement(searchResponse, trackingResults);
        setupClickTracking();
      })
      .catch((error: unknown) => {
        if (__DEBUG__) {
          console.error(
            `${CONSOLE_CAPTURE_PREFIX} Deferred injectable-unit placement failed.`,
            error,
          );
        }
      });

    // Wire up click pingbacks for the renderer's `data-beacon` markup (web
    // results, related searches, yahoo units). Runs once the DOM is ready; if
    // the document already parsed, run immediately. Must not wait on enrichment
    // — a slow/unreachable EE must not delay click tracking.
    if (document.readyState === "loading") {
      addEventListener("DOMContentLoaded", setupClickTracking, { once: true });
    } else {
      setupClickTracking();
    }

    // Post-load Enrichment Engine: fill SSR enrichment_slot placeholders.
    // Best-effort and fire-and-forget — do not block the rest of main() on the
    // enrich POST (hangs when ENRICHMENT_ENGINE_URL is unset/unreachable).
    // Gated slots are placed with the units, so injection waits for them.
    const enrichmentPromise = runEnrichment(
      server.search?.response,
      deferredPlacement,
    );

    if (__DEBUG__) {
      // Dynamically import and run async debug assertions
      await import("./debug/async-assertions").then((mod) =>
        mod.runAsyncDebugAssertions(server),
      );
      if (__DEBUG__) {
        const { getDefaultDebugTabs } = await loadDebuggerRuntime();
        void getDefaultDebugTabs();
        // Paint overlay with server data first; refresh when enrichment settles
        // so a hung enrich POST cannot stall the debug panel either.
        await showDebugOverlay({
          server,
          requestPayload,
          rampJsServerUrl,
        });
        void enrichmentPromise.then((enrichment) =>
          showDebugOverlay({
            server,
            requestPayload,
            rampJsServerUrl,
            enrichment,
          }),
        );
      }
    }
  } catch (error) {
    if (__DEBUG__) {
      console.error(
        `${CONSOLE_CAPTURE_PREFIX} Unhandled Exception was caught.`,
        error,
      );
      await showDebugOverlay({ error });
    }
  }
}
